// tslint:disable: cyclomatic-complexity

import React, { useCallback } from 'react';
import DataGrid, { Column, Button, Editing, GroupPanel, HeaderFilter, LoadPanel, Paging, Scrolling, SearchPanel, Selection, Sorting } from 'devextreme-react/data-grid';
import { IDynamicDataGridProperties } from './DynamicDataGrid.properties';
import { withStyles } from '@material-ui/core';
import styles from './DynamicDataGrid.styles';
import { useGridKeyboardNavigation } from '../DataGrid.hooks';
import { COLUMN_SIZE, DATA_GRID_LOADING_INDICATOR_HEIGHT_WIDTH, DEFAULT_PAGE_SIZE, NumberOfDecimalPlaces } from '../DataGrid.constants';
import MoreIcon from '@markinson/uicomponents-v2/SvgIcons/More';
import ProductCell from 'components/common/ProductCell';
import { HeaderFilterHeight, HeaderFilterWidth, EditDelay } from './DynamicDataGrid.constants';

const DynamicDataGrid = (props: IDynamicDataGridProperties) => {
    const { classes, gridProps, columns, allowHeaderFilting, allowDelete, selection, actionsColWidth, detailOnClick, calculateFieldValue, onDelete, actionsAtStart, actionsAtEnd, allowUpdating, actionButtonsVisibility, dataGridClassName, onEditClick = null, standardKeyboardNavigation = true } = props;
    const { dataGridRef, disableDrillDown, onEditorPreparing, onKeyDown, sorting, disableEditing, disableDelete, headerFilterHeight = HeaderFilterHeight, headerFilterWidth = HeaderFilterWidth, onContentReady, ...restGridProps } = gridProps;
    const { preventDataGridDefaults } = useGridKeyboardNavigation();

    const handleOnKeyDown = useCallback(
        async (e) => {
            preventDataGridDefaults(e);
            const dataGridInstance = dataGridRef.current?.instance;

            const getFocusedElement = () => dataGridInstance?.option('focusedElement') || document.activeElement;
            const isFocusedOnActionButton = () => {
                const focusedElement = getFocusedElement();

                return (
                    focusedElement &&
                    (focusedElement.classList?.contains('dx-command-edit') ||
                        focusedElement.querySelector('a.dx-icon-edit, a.dx-icon-trash'))
                );
            };

            // Standard Keyboard Navigation
            if (standardKeyboardNavigation) {
                const keyboardEvent = e.event as KeyboardEvent;
                const selKey = e.component.getSelectedRowKeys();

                if (keyboardEvent.key === 'ArrowUp' || keyboardEvent.key === 'ArrowDown') {
                    keyboardEvent.stopPropagation();
                    const visibleRowsLength = await e.component.getVisibleRows().length;

                    if (selKey.length) {
                        const currentKey = selKey[0];
                        let index = e.component.getRowIndexByKey(currentKey);
                        if (keyboardEvent.key === 'ArrowUp') {
                            index--;
                            if (index >= 0) {
                                await e.component.selectRowsByIndexes([index]);
                            }
                        } else if (keyboardEvent.key === 'ArrowDown') {
                            index++;
                            if (index < visibleRowsLength) {
                                await e.component.selectRowsByIndexes([index]);
                            }
                        }
                    }
                    await e.component.focus();
                }

                // Trigger Drilldown
                if (keyboardEvent.key === 'Enter' && keyboardEvent.shiftKey && keyboardEvent.ctrlKey && selKey.length) {
                    keyboardEvent.stopPropagation();
                    const visibleRows = e.component.getVisibleRows();
                    const selectedRow = visibleRows.find((row) => row.key === selKey[0]);

                    if (selectedRow && detailOnClick && !isFocusedOnActionButton()) {
                        const disableDetail = (selectedRow.data?.DisableLine ?? false) || disableDrillDown;
                        if (detailOnClick && !disableDetail) {
                            detailOnClick(selectedRow);
                        }
                    }

                    keyboardEvent.preventDefault();
                }

                // Trigger Edit Mode
                if (keyboardEvent.key === 'Enter' && keyboardEvent.ctrlKey && selKey.length) {
                    keyboardEvent.stopPropagation();
                    const visibleRows = e.component.getVisibleRows();
                    const selectedRow = visibleRows.find((row) => row.key === selKey[0]);

                    if (selectedRow && allowUpdating && !(selectedRow.data.DisableEditAction ?? disableEditing)) {
                        const rowIndex = e.component.getRowIndexByKey(selKey[0]);
                        if (rowIndex !== -1) {
                            const visibleColumns = e.component.getVisibleColumns();
                            const firstEditableColumnIndex = visibleColumns.findIndex((col) => col.allowEditing);
                            const cellElement = dataGridInstance.getCellElement(rowIndex, firstEditableColumnIndex);

                            if (cellElement && firstEditableColumnIndex !== -1) {
                                const scrollable = dataGridInstance.getScrollable();
                                if (scrollable) {
                                    scrollable.scrollToElement(cellElement);
                                }

                                setTimeout(
                                    () => {
                                        dataGridInstance.option('focusedColumnIndex', firstEditableColumnIndex);
                                        dataGridInstance.focus(cellElement);
                                        e.component.editRow(rowIndex);
                                    },
                                    EditDelay
                                );
                            }
                        }
                    }

                    keyboardEvent.preventDefault();
                }

                // Trigger Delete Action
                if (keyboardEvent.key === 'Delete' && selKey.length) {
                    const visibleRows = e.component.getVisibleRows();
                    const selectedRow = visibleRows.find((row) => row.key === selKey[0]);

                    if (selectedRow && onDelete && !(selectedRow.data.DisableDeleteAction ?? disableDelete)) {
                        onDelete(selKey[0], selectedRow.data);
                    }
                    keyboardEvent.preventDefault();
                }
            }

            if (onKeyDown) {
                onKeyDown(e);
            }
        },
        [onKeyDown, onDelete, detailOnClick]
    );

    const detailCellComponent = useCallback(
        (allProps) => {
            const disableDetail = (allProps?.data?.data?.DisableLine ?? false) || disableDrillDown;

            return <MoreIcon className={disableDetail ? classes.disabledLineDetailIcon : classes.lineDetailIcon} onClick={() => {
                if (detailOnClick) {
                    detailOnClick(allProps.data ? allProps.data : null);
                }
            }} />;
        },
        [disableDrillDown, detailOnClick]
    );

    const handleSetCellValue = useCallback(
        async (prevData, newData, field, value) => {
            if (calculateFieldValue) {
                const mergedData = { ...prevData, ...newData };
                mergedData[field] = value;
                const response = await calculateFieldValue(mergedData, field);
                const lineKey = response && Object.keys(response).find((key) => key.toLowerCase() !== 'status') || undefined;
                const lineData = (response?.Status ?? response?.status) && lineKey ? (response[lineKey]?.inlineObject ?? response[lineKey]) : prevData;

                columns.forEach((column) => {
                    const dataField = column?.dataField ?? '';
                    newData[dataField] = lineData?.[dataField];
                });
            }
        },
        [calculateFieldValue, columns]
    );

    const validateEditing = useCallback((e: any) => e.row.data.DisableEditAction ?? disableEditing ?? false, [disableEditing]);

    const validateDelete = useCallback((e) => e.row.data.DisableDeleteAction ?? disableDelete ?? false, [disableDelete]);

    const deleteClick = useCallback(
        async (e) => {
            if (onDelete) {
                onDelete(e.row.key, e.row.data);
            }
        },
        [onDelete]
    );

    const editClick = useCallback(
        async (e) => {
            if (onEditClick) {
                onEditClick(e.row.key, e.row.data);
            }
        },
        [onEditClick]
    );

    const renderActionsAtStart = useCallback((rowData) => actionsAtStart ? actionsAtStart(rowData) : null, [actionsAtStart]);

    const renderActionsAtEnd = useCallback((rowData) => actionsAtEnd ? actionsAtEnd(rowData) : null, [actionsAtEnd]);

    const renderCellComponent = useCallback(
        (rowData, col) => {
            if (col?.cellComponent) {
                return col?.cellComponent(rowData);
            }

            return undefined;
        },
        []
    );

    return (
        <DataGrid
            {...restGridProps}
            ref={dataGridRef}
            className={`${dataGridClassName || classes.dataGrid} ${classes.gridDefaults}`}
            noDataText=''
            showBorders={false}
            renderAsync={true}
            repaintChangesOnly={true}
            allowColumnReordering={true}
            remoteOperations={true}
            allowColumnResizing={true}
            columnResizingMode={'nextColumn'}
            hoverStateEnabled={true}
            onEditorPreparing={(e) => {
                e.editorOptions.onFocusIn = (args) => {
                    const input = args.element.querySelector("input[type='text']");
                    if (input) {
                        input.select();
                    }
                };

                if (onEditorPreparing) {
                    onEditorPreparing(e);
                }
            }}
            onContentReady={(e) => {
                setTimeout(
                    () => {
                        const headerCells = e.element?.querySelectorAll('td[role="columnheader"]');
                        headerCells?.forEach((headerCell) => {
                            headerCell.setAttribute('tabindex', '-1');
                        });
                    },
                    0
                );

                if (onContentReady) {
                    onContentReady(e);
                }
            }}
            onKeyDown={handleOnKeyDown}>
            <Paging defaultPageSize={DEFAULT_PAGE_SIZE} defaultPageIndex={0} />
            <Editing
                refreshMode={'reshape'}
                mode='row'
                allowAdding={false}
                allowDeleting={allowDelete ?? true}
                allowUpdating={allowUpdating}
                confirmDelete={false}
            />
            <HeaderFilter
                visible={allowHeaderFilting}
                width={headerFilterWidth}
                height={headerFilterHeight}
            />
            {detailOnClick && (<Column
                allowEditing={false}
                width={COLUMN_SIZE.sm0}
                allowResizing={false}
                fixed={true}
                alignment='center'
                allowHeaderFiltering={false}
                cellComponent={detailCellComponent} />)
            }

            {columns?.map((col) => (col?.caption === 'Product') ?
                <Column
                    {...col}
                    caption={'Product'}
                    allowEditing={false}
                    allowHeaderFiltering={false}
                    fixed={col?.fixed ?? true}
                    cellComponent={(allProps) => (
                        <ProductCell
                            urlParamKeys={['ProductId']}
                            toLink={'/inventory-enquiry/product-details'}
                            {...allProps} />
                    )}
                />
                : <Column
                    {...col}
                    editorOptions={{
                        ...col?.isEditable || col?.allowEditing ? { step: 0 } : { disabled: true },
                        ...col?.editorOptions
                    }}
                    setCellValue={col?.isEditable ? async (newData, value: number, prevData): Promise<void> => {
                        await handleSetCellValue(prevData, newData, col?.dataField, value);
                    } : undefined}

                    calculateDisplayValue={
                        col?.shouldFixToDecimalPlaces ? (data: Record<string, unknown>) =>
                            (data?.[col?.dataField] as number)?.toFixed(data?.UnitConversionMaximumFractionDigits as number ?? NumberOfDecimalPlaces) : undefined
                    }
                    headerFilter={col?.headerFiltersDataSource ? { dataSource: col?.headerFiltersDataSource } : null}
                    cellComponent={col?.cellComponent ? (rowdata) => renderCellComponent(rowdata, col) : undefined}
                />
            )}

            <Column type={'buttons'} width={actionsColWidth ?? COLUMN_SIZE.sm1} allowResizing={false} fixed={true} visible={actionButtonsVisibility ?? true}>
                <Button render={renderActionsAtStart} />
                <Button name={'save'} icon={'save'} />
                {
                    onEditClick ?
                        <Button name={'edit'} icon={'edit'} disabled={validateEditing} cssClass={classes.actionIcon} onClick={editClick} />
                        :
                        <Button name={'edit'} icon={'edit'} disabled={validateEditing} cssClass={classes.actionIcon} />
                }

                <Button name={'delete'} icon={'trash'} disabled={validateDelete} cssClass={classes.actionIcon} onClick={deleteClick} />
                <Button name={'cancel'} icon={'undo'} />
                <Button name={'undelete'} icon={'undo'} />
                <Button render={renderActionsAtEnd} />
            </Column>
            <GroupPanel visible={false} />
            <SearchPanel visible={false} />
            <Scrolling mode={'infinite'} />
            <Selection mode={standardKeyboardNavigation ? 'single' : (selection ?? 'none')} showCheckBoxesMode={selection === 'multiple' ? 'always' : 'none'} />
            <Sorting mode={sorting?.mode ?? 'none'} />
            <LoadPanel
                shading={false}
                height={DATA_GRID_LOADING_INDICATOR_HEIGHT_WIDTH}
                width={DATA_GRID_LOADING_INDICATOR_HEIGHT_WIDTH}
                text={''}
                showPane={false} />
        </DataGrid>
    );
};

export default React.memo(withStyles(styles, { index: 1 })(DynamicDataGrid));
